import axios from "axios";
import jwt_decode from "jwt-decode";
import configJson from "../../config.json";
import { actions as authActions } from "../store/ducks/auth.duck";

const normalizedUrlBase = (configJson?.urlBase || "").replace(/\/$/, "");
const refreshUrl = normalizedUrlBase ? `${normalizedUrlBase}/auth/refresh` : "/auth/refresh";
const refreshClient = axios.create();
let refreshPromise = null;

const normalizeTokenValue = token => {
  if (typeof token !== "string") {
    return null;
  }
  const cleaned = token.replace(/['"]/g, "").trim();
  if (!cleaned) {
    return null;
  }
  const bearerMatch = cleaned.match(/^Bearer\s+(.+)$/i);
  return bearerMatch ? bearerMatch[1].trim() : cleaned;
};

const buildBearerHeader = token => {
  const normalized = normalizeTokenValue(token);
  return normalized ? `Bearer ${normalized}` : undefined;
};

const updateStoredUserDetailsToken = newToken => {
  if (!newToken) {
    return;
  }

  try {
    const storedDetails = window.localStorage.getItem("userDetails");
    if (!storedDetails) {
      return;
    }

    const parsedDetails = JSON.parse(storedDetails);
    if (parsedDetails && typeof parsedDetails === "object") {
      window.localStorage.setItem(
        "userDetails",
        JSON.stringify({ ...parsedDetails, access_token: newToken })
      );
    }
  } catch (storageError) {
    console.warn("Unable to refresh stored user details:", storageError);
  }
};

const handleAuthFailure = store => {
  if (store) {
    store.dispatch(authActions.logout());
  }
  try {
    window.localStorage.removeItem("persist:demo4-auth");
    window.localStorage.removeItem("userDetails");
  } catch (storageError) {
    console.warn("Unable to clear stored auth data:", storageError);
  }
  window.location.href = "/auth/login";
};

const queueTokenRefresh = (currentToken, store) => {
  if (!currentToken) {
    return Promise.reject(new Error("Missing token for refresh"));
  }

  if (refreshPromise) {
    return refreshPromise;
  }

  const header = buildBearerHeader(currentToken);
  if (!header) {
    return Promise.reject(new Error("Invalid token for refresh"));
  }

  refreshPromise = refreshClient
    .get(refreshUrl, {
      headers: { Authorization: header },
    })
    .then(({ data }) => {
      const refreshedToken = normalizeTokenValue(data?.result?.access_token);
      if (!refreshedToken) {
        throw new Error("Missing refreshed authentication token");
      }
      if (store) {
        store.dispatch(authActions.refreshToken(refreshedToken));
      }
      updateStoredUserDetailsToken(refreshedToken);
      return refreshedToken;
    })
    .catch(error => {
      if (store) {
        handleAuthFailure(store);
      }
      throw error;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

const evaluateTokenState = token => {
  try {
    const decoded = jwt_decode(token);
    const now = Math.floor(Date.now() / 1000);
    const exp = Number(decoded?.exp);
    const iat = Number(decoded?.iat);

    if (exp && now >= exp) {
      return { expired: true };
    }

    if (exp && iat && exp > iat) {
      const lifetime = exp - iat;
      const refreshThreshold = exp - Math.floor(lifetime * 0.2);
      if (now >= refreshThreshold && now < exp) {
        return { shouldRefresh: true };
      }
    }
  } catch (decodeError) {
    // Ignore decode errors; the API will respond with 401 if the token is invalid.
  }

  return {};
};

export function setupAxios(axiosInstance, store) {
  axiosInstance.interceptors.request.use(
    async config => {
      if (!store) {
        return config;
      }

      const state = store.getState();
      const normalizedToken = normalizeTokenValue(state?.auth?.authToken);
      if (!normalizedToken) {
        return config;
      }

      let activeToken = normalizedToken;
      const tokenState = evaluateTokenState(activeToken);
      if (tokenState.expired) {
        handleAuthFailure(store);
        throw new Error("Access token expired");
      }

      if (tokenState.shouldRefresh) {
        activeToken = await queueTokenRefresh(activeToken, store);
      }

      const headerValue = buildBearerHeader(activeToken);
      if (headerValue) {
        config.headers.Authorization = headerValue;
      }

      return config;
    },
    err => Promise.reject(err)
  );

  axiosInstance.interceptors.response.use(
    response => response,
    error => {
      if (error.response && error.response.status === 401) {
        handleAuthFailure(store);
      }

      return Promise.reject(error);
    }
  );
}
